import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

// The "Block advertiser" dialog lives in FsboAdvertiserStatus (Sellers card)
// but is also reachable from the reject dialog in ListingStatusSelect
// (#24256): "block the advertiser instead of rejecting". Both sit under the
// listing page, so a tiny context carries the open flag and whether a card
// that can act is mounted at all. Outside the provider the hook returns
// null and the reject dialog simply offers no such link.
type FsboBlockDialogState = {
  /** A block card is mounted, the viewer may act, and nobody is blocked yet. */
  available: boolean;
  open: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  setAvailable: (available: boolean) => void;
};

const FsboBlockDialogContext = createContext<FsboBlockDialogState | null>(null);

export const FsboBlockDialogProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState(false);
  const openDialog = useCallback(() => setOpen(true), []);
  const closeDialog = useCallback(() => setOpen(false), []);
  const value = useMemo(
    () => ({ available, open, openDialog, closeDialog, setAvailable }),
    [available, open, openDialog, closeDialog],
  );
  return (
    <FsboBlockDialogContext.Provider value={value}>
      {children}
    </FsboBlockDialogContext.Provider>
  );
};

export const useFsboBlockDialog = (): FsboBlockDialogState | null =>
  useContext(FsboBlockDialogContext);
